// ============================================================
// Tracking: Meta Pixel + GA4 + UTM/FBCLID attribution capture
// No IDs are hard-coded. Set them in .env:
//   VITE_META_PIXEL_ID=...
//   VITE_GA_MEASUREMENT_ID=...
// If a variable is missing, nothing loads and nothing errors.
// ============================================================

const META_PIXEL_ID = (import.meta.env.VITE_META_PIXEL_ID as string | undefined)?.trim() || "";
/** GA4 measurement ID, from the linked Google Analytics connector (or manual .env fallback). */
const GA_ID =
  (import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY as string | undefined)?.trim() ||
  (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)?.trim() ||
  "";

const STORAGE_KEY = "sh_attribution";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

export interface Attribution {
  landing_page: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  fbclid: string;
}

const emptyAttribution = (): Attribution => ({
  landing_page: typeof window === "undefined" ? "" : window.location.pathname,
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
  utm_content: "",
  utm_term: "",
  fbclid: "",
});

/** Reads utm parameters and fbclid once per session, keeping them for the visit. */
export const captureAttribution = (): Attribution => {
  if (typeof window === "undefined") return emptyAttribution();
  let stored: Attribution | null = null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) stored = JSON.parse(raw) as Attribution;
  } catch {
    stored = null;
  }

  const params = new URLSearchParams(window.location.search);
  const fresh = emptyAttribution();
  let hasFresh = false;
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) {
      fresh[k] = v.slice(0, 200);
      hasFresh = true;
    }
  });
  const fbclid = params.get("fbclid");
  if (fbclid) {
    fresh.fbclid = fbclid.slice(0, 400);
    hasFresh = true;
  }

  const result = hasFresh || !stored ? { ...(stored ?? {}), ...fresh } : stored;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(result));
  } catch {
    /* private mode — attribution simply isn't persisted */
  }
  return result as Attribution;
};

export const getAttribution = (): Attribution => captureAttribution();

/* -------------------- script loading -------------------- */

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; callMethod?: unknown };
    _fbq?: unknown;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialised = false;

const loadMetaPixel = () => {
  if (!META_PIXEL_ID || window.fbq) return;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const n: any = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  };
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  window.fbq = n;
  window._fbq = n;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(s);
  window.fbq?.("init", META_PIXEL_ID);
};

const loadGA = () => {
  if (!GA_ID || window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  const gtag = (...args: unknown[]) => {
    window.dataLayer!.push(args);
  };
  window.gtag = gtag;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  gtag("js", new Date());
  gtag("config", GA_ID);
};

/** Safe to call on every page; loads only what is configured, once. */
export const initTracking = () => {
  if (typeof window === "undefined") return;
  captureAttribution();
  if (initialised) return;
  initialised = true;
  loadMetaPixel();
  loadGA();
  trackMeta("PageView", undefined, "PageView");
};

/* -------------------- events -------------------- */

const fired = new Set<string>();

/** Fires a Meta event. `onceKey` makes it fire at most once per page load. */
export const trackMeta = (
  event: string,
  params?: Record<string, unknown>,
  onceKey?: string,
  custom = false
) => {
  if (onceKey) {
    if (fired.has(onceKey)) return;
    fired.add(onceKey);
  }
  window.fbq?.(custom ? "trackCustom" : "track", event, params);
};

/** Fires a GA4 event (no-op when GA is not configured). */
export const trackGA = (event: string, params?: Record<string, unknown>) => {
  window.gtag?.("event", event, params ?? {});
};

export const trackLeadFormOpen = (source: string) => {
  trackGA("lead_form_open", { source });
  trackMeta("PriceRequest", { source }, `PriceRequest:${source}`, true);
};

export const trackLeadSubmitted = (payload: Record<string, unknown>) => {
  trackGA("lead_form_submit", payload);
  trackMeta("Lead", { content_name: "Siam Horizon Villa Enquiry" }, "Lead");
  trackMeta("BrochureRequest", undefined, "BrochureRequest", true);
  trackMeta("FloorPlanRequest", undefined, "FloorPlanRequest", true);
};

export const trackContactClick = (channel: "line" | "whatsapp" | "messenger" | "phone" | "email") => {
  trackGA(`contact_${channel}`);
  trackMeta("Contact", { channel }, `Contact:${channel}`);
};

export const trackPrivatePreview = (source: string) => {
  trackGA("private_preview_click", { source });
  trackMeta("Schedule", { content_name: "Private preview" }, "Schedule");
  trackMeta("PrivatePreviewRequest", { source }, "PrivatePreviewRequest", true);
};

export const trackViewContent = (name: string) => {
  trackMeta("ViewContent", { content_name: name }, `ViewContent:${name}`);
};

export const trackDocument = (kind: "brochure" | "floorplan") => {
  trackGA(kind === "brochure" ? "brochure_request" : "floorplan_request");
  trackMeta(kind === "brochure" ? "BrochureRequest" : "FloorPlanRequest", undefined, undefined, true);
};
